import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  GetNotificationSettingsAction,
  SaveNotificationSettingsAction,
} from "../store/action";
import { notify, notifySaveError } from "../utils/notify";

/**
 * The organization's notification settings, as one form shared by two Settings
 * tabs: Notifications (what is sent, on which channel) and Language & time zone
 * (what language it is written in, which clock "24 hours before" is measured
 * on).
 *
 * They are one form because they are one API record — one GET, one PUT that
 * replaces every field. Two independent forms would each send the other's
 * fields as they were when the page loaded, so saving one tab would quietly
 * undo an unsaved change on the other. Held here, above both tabs, a save from
 * either sends everything the reader has changed, and switching tabs never
 * discards an edit.
 *
 * `enabled` is false for anyone without an organization (customers, platform
 * staff): the endpoint is gated to organization members and would only 403.
 */
export const useNotificationSettings = (enabled) => {
  const { t } = useTranslation();

  const [form, setForm] = useState(null);
  const [allowedOffsets, setAllowedOffsets] = useState([]);
  const [availableLocales, setAvailableLocales] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [saving, setSaving] = useState(false);
  /**
   * The load failure, as a translation key. Held unresolved so `load` does not
   * have to close over `t` — see the comment there. A failed *save* is not
   * held here: it goes to the snackbar (utils/notify.js).
   */
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await GetNotificationSettingsAction();
      setAllowedOffsets(data.allowedReminderOffsets ?? []);
      setAvailableLocales(data.availableLocales ?? []);
      setForm({
        customerConfirmationEmail: data.customerConfirmationEmail,
        customerReminderEmail: data.customerReminderEmail,
        customerChangeEmail: data.customerChangeEmail,
        customerConfirmationSms: data.customerConfirmationSms ?? false,
        customerReminderSms: data.customerReminderSms ?? false,
        customerChangeSms: data.customerChangeSms ?? false,
        reminderPrimaryMinutes: data.reminderPrimaryMinutes,
        reminderSecondaryMinutes: data.reminderSecondaryMinutes,
        replyToEmail: data.replyToEmail ?? "",
        staffEmailOnNewBooking: data.staffEmailOnNewBooking,
        staffBrowserOnNewBooking: data.staffBrowserOnNewBooking,
        staffBrowserOnCancellation: data.staffBrowserOnCancellation,
        timeZoneId: data.timeZoneId,
        locale: data.locale,
      });
    } catch (err) {
      // 403 = the organization isn't set up yet (mid-onboarding). Nothing has
      // been misconfigured, so there is nothing to warn about.
      if (err?.response?.status === 403) {
        setForm(null);
      } else {
        setError("Dashboard.Notifications.loadError");
      }
    } finally {
      setLoading(false);
    }
    // No dependencies on purpose. Translating at render time instead of here
    // keeps `t` out of this callback — an unstable `t` would give `load` a new
    // identity on every render, and the effect below would then re-run forever.
  }, []);

  useEffect(() => {
    if (enabled) load();
    else setLoading(false);
  }, [enabled, load]);

  const setField = useCallback((name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const data = await SaveNotificationSettingsAction({
        ...form,
        // Blank means "fall back to the public contact address"; the API stores
        // that as null.
        replyToEmail: form.replyToEmail.trim() || null,
      });
      // Re-seed from the response: the API normalises the pair of reminders
      // (promoting a lone secondary, ordering them longest-first), so the form
      // must show what was actually stored, not what was submitted.
      setForm((current) => ({
        ...current,
        reminderPrimaryMinutes: data.reminderPrimaryMinutes,
        reminderSecondaryMinutes: data.reminderSecondaryMinutes,
        replyToEmail: data.replyToEmail ?? "",
        timeZoneId: data.timeZoneId,
        locale: data.locale,
      }));
      notify.success(t("Dashboard.Notifications.saved"));
    } catch (err) {
      notifySaveError(err, t("Dashboard.Notifications.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return {
    form,
    setField,
    allowedOffsets,
    availableLocales,
    loading,
    saving,
    error,
    save,
  };
};
